import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./MissingWord.css";

const WORD_BANK = [
  "CASA", "RAMA", "MANO", "PASA", "RATA", "SOPA", "PINO", "LINO",
  "CASETA", "PILETA", "PALETA", "MALETA", "CARETA", "MERCADO", "PIRULETA",
  "LIBRETA", "SALERO", "PLUMERO", "TROMPETA", "CARPETA", "PLANETA",
  "ESCALADA", "TONELADA", "CUNETA", "SEGURO", "PELUDO", "APURO", "CANGURO",
  "EMPATIZAR", "HABLAR", "TRABAJAR", "TRATAR", "CONTAR", "MANGUERA",
  "ESCALERA", "PANADERA", "MANERA", "REGADERA", "MUELA", "ABUELA",
  "CAZUELA", "ESCUELA", "VUELA", "HUERTA", "AVIONETA", "HUESO", "PUERTA",
  "ALERTA", "CALENTADOR", "SALTADOR", "ENCANTADOR", "DESCUBRIDOR",
  "DESPERTADOR", "AGUJETA", "CAMIONETA", "ESCOPETA", "CORTAS", "BROTAS",
  "CAUTAS", "CARTAS", "AGOTAS"
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    leftCount: 3,
  },
  medium: {
    label: "Media",
    totalRounds: 10,
    leftCount: 4,
  },
  hard: {
    label: "Difícil",
    totalRounds: 12,
    leftCount: 5,
  },
};

function shuffle(list) {
  return [...list].sort(() => Math.random() - 0.5);
}

function createRound(config) {
  const leftWords = shuffle(WORD_BANK).slice(0, config.leftCount);
  const missingWord = leftWords[Math.floor(Math.random() * leftWords.length)];

  const rightWords = shuffle(
    leftWords.filter((word) => word !== missingWord)
  );

  return {
    leftWords: shuffle(leftWords),
    rightWords,
    missingWord,
  };
}

export default function MissingWord() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correct, setCorrect] = useState(0);
  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((acc, item) => acc + item.reactionTime, 0) /
            responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setRoundIndex(0);
    setCorrect(0);
    setErrors(0);
    setResponses([]);
    setStarted(true);
    setFinished(false);
    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
    setRound(createRound(nextConfig));
  };

  const answer = (word) => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;
    const isCorrect = word === round.missingWord;

    setResponses((prev) => [
      ...prev,
      {
        round: roundIndex + 1,
        answer: word,
        correctAnswer: round.missingWord,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) setCorrect((prev) => prev + 1);
    else setErrors((prev) => prev + 1);

    const nextRound = roundIndex + 1;

    if (nextRound >= config.totalRounds) {
      setFinished(true);
      setStarted(false);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRound);
    setRound(createRound(config));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setStarted(false);
    setFinished(false);
    setRound(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Palabra ausente"
        description="Compara las palabras de ambos lados y pulsa cuál falta en la derecha."
        areas={["Atención selectiva", "Rastreo visual", "Lectura"]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="missing-word-page">
      <div className="missing-word-card">
        <header className="missing-word-header">
          <div>
            <h1>Palabra ausente</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="missing-word-instruction">
              <h2>¿Qué palabra falta en la derecha?</h2>
            </section>

            <section className="missing-word-stage">
              <div className="word-panel">
                <span className="word-panel-title">Izquierda</span>

                <div className="word-list">
                  {round.leftWords.map((word, index) => (
                    <button
                      key={`${word}-${index}`}
                      type="button"
                      className="word-button"
                      onClick={() => answer(word)}
                    >
                      {word}
                    </button>
                  ))}
                </div>
              </div>

              <div className="word-panel right-word-panel">
                <span className="word-panel-title">Derecha</span>

                <div className="word-list">
                  {round.rightWords.map((word, index) => (
                    <div key={`${word}-${index}`} className="word-display">
                      {word}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="missing-word-score">
              <span>Aciertos: {correct}</span>
              <span>Errores: {errors}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Errores", value: errors },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}